import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Close from '@mui/icons-material/Close';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import superagent from 'superagent';
import {Typography} from '@mui/material';
import {QEPInput} from '@qep/shared/src/components/QEPInput/qep-input';
import {getQepSystem} from '@qep/shared/src';
import {getApiServer, getAppEnv} from '@qep/shared/src/utils/runtime-config';
import {requestServiceWorkerVersion} from '../../utils/request-service-worker-version.js';
import QEPPreferences from './qep/index.js';
import TradeProductsPreferences from './trade-products/index.js';
import {OrderBookPreferences} from './order-book/preferences.js';
import {ColumnPreferences} from './columns/column-preferences.js';

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 750,
  height: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 0,
};

const closeStyles = {
  position: 'absolute',
  height: 31,
  width: 31,
  right: 0,
};

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};
export const getAppLabel = (swVersion: string) => {
  const {appVersion} = globalThis as unknown as {appVersion: string};
  const appEnv = getAppEnv();
  if (!appEnv) {
    return `${appVersion}:${swVersion}`;
  }

  switch (appEnv) {
    case 'local': {
      return `local:${appVersion}:${swVersion}`;
    }

    case 'development': {
      return `development:${appVersion}:${swVersion}`;
    }

    case 'staging': {
      return `staging:${appVersion}:${swVersion}`;
    }

    case 'preprod': {
      return `preprod:${appVersion}:${swVersion}`;
    }

    default: {
      return `${appVersion}:${swVersion}`;
    }
  }
};

function CustomTabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`preferences-tabpanel-${index}`}
      aria-labelledby={`preferences-tab-${index}`}
      style={{
        height: '100%',
      }}
      {...other}
    >
      {value === index && <Box sx={{p: 3, height: '100%'}}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `preferences-tab-${index}`,
    'aria-controls': `preferences-tabpanel-${index}`,
  };
}

type Props = {
  open: boolean;
  onClose: () => void;
};

export function PreferencesModal(props: Props) {
  const [value, setValue] = useState(0);
  const {open, onClose} = props;
  // Feature-flagged Columns preferences tab. When the flag is disabled we
  // hide the Tab control and its corresponding CustomTabPanel entirely so
  // the ColumnPreferences tree never mounts.
  const canUseColumnsPreferences = getQepSystem().container.canUseFeature('preferencesColumnsTab');
  const isColumnsTab = canUseColumnsPreferences && value === 3;
  const modalStyle = {
    ...style,
    height: isColumnsTab ? 'min(800px, 80vh)' : 400,
    maxHeight: '80vh',
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby='preferences-modal-title'
      aria-describedby='preferences-modal-description'
    >
      <Box sx={modalStyle}>
        <Stack direction='row' sx={{height: '100%'}}>
          <Stack direction='column' sx={{borderRight: 1, borderColor: 'divider', flexShrink: 0}}>
            <Box sx={{p: 2}}>
              <Typography variant='h6'>Preferences</Typography>
            </Box>

            <Tabs value={value} onChange={handleChange} aria-label='preference tabs' orientation='vertical'>
              <Tab sx={{alignItems: 'end'}} label='QEP' {...a11yProps(0)} />
              <Tab sx={{alignItems: 'end'}} label='Trade Products' {...a11yProps(1)} />
              <Tab sx={{alignItems: 'end'}} label='Order Book' {...a11yProps(2)} />
              {canUseColumnsPreferences && (
                <Tab sx={{alignItems: 'end'}} label='Columns' {...a11yProps(3)} />
              )}
            </Tabs>
          </Stack>

          <Stack sx={{flexGrow: 1, overflow: 'auto'}} direction='column'>
            <CustomTabPanel value={value} index={0}>
              <QEPPreferences />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
              <TradeProductsPreferences />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2}>
              <OrderBookPreferences />
            </CustomTabPanel>
            {canUseColumnsPreferences && (
              <CustomTabPanel value={value} index={3}>
                <ColumnPreferences />
              </CustomTabPanel>
            )}
          </Stack>

          <IconButton sx={closeStyles} onClick={onClose}><Close /></IconButton>
        </Stack>
        <FakeUser />
      </Box>
    </Modal>
  );
}

const validateIsEmail = (email: string) => {
  const re = /\S+@\S+\.\S+/;
  return re.test(email);
};

const FakeUser = () => {
  const [newUser, setNewUser] = useState('');
  const [creds, setCreds] = useState<{sessionId: string; upn?: string; traderId?: string} | undefined>(undefined);
  const [appLabel, setAppLabel] = useState('');
  const serviceWorkerVersionRef = useRef('');

  useEffect(() => {
    const fetchCreds = async () => {
      const creds = await getCreds() as {sessionId: string};
      setCreds(creds);
    };

    void fetchCreds();
  }, []);

  // Need to know the user information to display the preferences
  const submitUser = useCallback(() => {
    if (!validateIsEmail(newUser)) {
      return;
    }

    const updateInfo = async () => {
      await superagent('POST', getApiServer() + '/api/auth/fake')
        .set('x-qep-session-id', creds?.sessionId ?? '')
        .type('form')
        .withCredentials()
        .send({user: newUser});
    };

    void updateInfo();
  }, [newUser]);
  const updateUser = useCallback(() => {
    submitUser();
  }, [newUser]);
  useEffect(() => {
    const syncAppLabel = () => {
      setAppLabel(getAppLabel(serviceWorkerVersionRef.current));
    };

    syncAppLabel();
    requestServiceWorkerVersion(version => {
      serviceWorkerVersionRef.current = version;
      syncAppLabel();
    });

    const intervalId = globalThis.setInterval(() => {
      if (getAppEnv()) {
        syncAppLabel();
        globalThis.clearInterval(intervalId);
      }
    }, 250);

    return () => {
      globalThis.clearInterval(intervalId);
    };
  }, []);

  return <div >
    <span>
      <span>Current User:</span>
      <span>{`${creds?.upn}::${creds?.traderId}`}</span>
    </span>
    {creds?.upn?.toLowerCase() === 'ryan_sharp@cargill.com'
      ? <><span style={{paddingRight: '10px'}}>fake user</span><QEPInput style={{color: 'white', marginRight: '10px'}} onChange={event => {
        setNewUser(event.target.value);
      }}/><button onClick={updateUser}>Update Session with user</button></>
      : null}
    <div>{appLabel}</div>
  </div>;
};

const getCreds = async () => new Promise((resolve, reject) => {
  void getQepSystem().container.authentication.getCurrentCredentials((error, credentials) => {
    if (error) {
      reject(error);
      return;
    }

    resolve(credentials.credentials ?? credentials);
  });
});
